import { Text } from "@amzn/stencil-react-components/text";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
type CountdownDisplayProps = {
  durationSeconds: number;
  timerFinishedCallback: () => void;
};

export const TIMER_CACHED_START_TIME_KEY = "TIMER_CACHED_START_TIME";
/**
 * Returns either the cached timer start time OR the param `now` (Pass a Date.now() const to ensure consistency) if there is no cached time.
 */
const getTimerStartTime = (now: number) => {
  const val = localStorage.getItem(TIMER_CACHED_START_TIME_KEY);
  if (val) {
    try {
      if (!isNaN(Number(val))) {
        return Number(val);
      }
    } catch {
      // no op
    }
  }
  return now;
};

/**
 * Provides a countdown timer that shows hours:minutes:seconds. Returns just text.
 * TODO: UX has not provided feedback on this yet. Follow up with unit tests when UX is finalized.
 * @param durationSeconds How long should the timer be? 600 results in a 10-minute countdown timer.
 * @param timerFinishedCallback Function to call if the timer reaches zero.
 */
export const CountdownDisplay = ({
  durationSeconds,
  timerFinishedCallback,
}: CountdownDisplayProps) => {
  // cache this value where it should be set the same for each.
  const nowOnInit = Date.now();
  /**
   * Make ref once mounted instead of recalculating in the future.
   * Optionally, pull the a cached timer start date to prevent the timer from restarting from now+durationSeconds on page refresh since the back end timer limit is still enforced either way.
   */
  const endTime = useRef(getTimerStartTime(nowOnInit) / 1000 + durationSeconds);

  const [secondsRemaining, setSecondsRemaining] = useState<number>(
    endTime.current - nowOnInit / 1000
  );

  // Set up a timer and then clear it when the component dismounts.
  useEffect(() => {
    // Cache the start time once.
    if (!localStorage.getItem(TIMER_CACHED_START_TIME_KEY)) {
      localStorage.setItem(TIMER_CACHED_START_TIME_KEY, String(nowOnInit));
    }

    // 600 is about where the user can't tell a difference in the seconds counter visual component. Use 400 for an even smoother seconds transition visual experience.
    const intervalMillis = 400;
    const interval = setInterval(() => {
      // Use current Date.now() not cached Date.time().
      const remainingSeconds = endTime.current - Date.now() / 1000;
      if (remainingSeconds < 1) {
        localStorage.removeItem(TIMER_CACHED_START_TIME_KEY);
        // IVVServiceIntegration will set a localStorage value when this timer finished callback occurs and that key
        // is checked before this component is rendered, elsewhere up the render chain.
        timerFinishedCallback();
        clearInterval(interval);
      }

      setSecondsRemaining(remainingSeconds);
    }, intervalMillis);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <Text dataTestId="CountdownTimerDisplay">
      {String(Math.floor(secondsRemaining / 60) % 60).padStart(1, "0")}:
      {String(Math.floor(secondsRemaining) % 60).padStart(2, "0")}
      <FormattedMessage id="identityCheck-ivv-countDownTimer" />
    </Text>
  );
};
